/**
 * Host tools for claude-code/* — the Agent SDK runs its OWN tool loop and rejects host tool schemas, so
 * agentx-only tools Claude Code has no native twin for (memory: Recall/Remember/MemorySearch) never reached
 * it ("no durable-memory tool"). This exposes them as an in-process SDK MCP server (`{type:'sdk', instance}`):
 * the SDK only calls `instance.connect(transport)` and exchanges JSON-RPC, so a tiny dependency-free server
 * is enough (no @modelcontextprotocol/sdk / zod — src stays edge-safe). Calls run through the host's dispatch,
 * the same path the cursor bridge uses.
 */
import type { AgentTool } from './tools.js';

/** agentx tools with no Claude Code native counterpart — bridged to claude-code/*. */
export const CC_BRIDGED = new Set(['Recall', 'Remember', 'MemorySearch']);

type Result = string | { text: string; images?: { mimeType: string; data: string }[] };
type Msg = { jsonrpc: '2.0'; id?: string | number | null; method?: string; params?: any };
interface Transport { start?(): Promise<void>; send(m: unknown): Promise<void>; onmessage?: (m: Msg) => void }

export function ccToolServer(name: string, tools: () => AgentTool[], call: (name: string, args: Record<string, any>) => Promise<Result>) {
  const instance = {
    async connect(t: Transport) {
      const reply = (id: Msg['id'], body: object) => t.send({ jsonrpc: '2.0', id, ...body });
      t.onmessage = async (m) => {
        if (m.id === undefined || m.id === null) return; // notifications (initialized, cancelled) need no reply
        try {
          switch (m.method) {
            case 'initialize':
              return await reply(m.id, { result: { protocolVersion: m.params?.protocolVersion ?? '2025-06-18', capabilities: { tools: {} }, serverInfo: { name, version: '1' } } });
            case 'tools/list':
              return await reply(m.id, { result: { tools: tools().map((x) => ({ name: x.name, description: x.description, inputSchema: x.parameters })) } });
            case 'tools/call': {
              const r = await call(m.params?.name, m.params?.arguments ?? {});
              const text = typeof r === 'string' ? r : r.text;
              const images = typeof r === 'string' ? [] : (r.images ?? []).map((i) => ({ type: 'image', mimeType: i.mimeType, data: i.data }));
              return await reply(m.id, { result: { content: [{ type: 'text', text }, ...images] } });
            }
            case 'ping':
              return await reply(m.id, { result: {} });
            default:
              return await reply(m.id, { error: { code: -32601, message: `method not found: ${m.method}` } });
          }
        } catch (e) {
          // A tool failure is a tool result the model can read, not a protocol error.
          if (m.method === 'tools/call') return reply(m.id, { result: { content: [{ type: 'text', text: String((e as Error)?.message ?? e) }], isError: true } });
          return reply(m.id, { error: { code: -32603, message: String((e as Error)?.message ?? e) } });
        }
      };
      await t.start?.();
    },
    async close() {},
  };
  return { type: 'sdk' as const, name, instance };
}
